import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Users, Award, Heart, DollarSign } from 'lucide-react';

export default function CareersPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: 'VA',
    zipCode: '',
    experience: '',
    availability: '',
    openAvailability: false,
    reference1Name: '',
    reference1Phone: '',
    reference2Name: '',
    reference2Phone: '',
    additionalInfo: '',
    backgroundConsent: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.backgroundConsent) {
      toast.error('Please consent to background check to continue');
      return;
    }
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/careers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Application submitted! We\'ll contact you soon.');
        // Reset form
        setFormData({
          fullName: '',
          phone: '',
          email: '',
          address: '',
          city: '',
          state: 'VA',
          zipCode: '',
          experience: '',
          availability: '',
          reference1Name: '',
          reference1Phone: '',
          reference2Name: '',
          reference2Phone: '',
          additionalInfo: '',
          openAvailability: false,
          backgroundConsent: false
        });
      } else {
        toast.error(result.message || 'Failed to submit application. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application. Please try again.');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const benefits = [
    {
      icon: DollarSign,
      title: 'Competitive Pay',
      description: 'Fair compensation for your hard work and dedication'
    },
    {
      icon: Users,
      title: 'Great Team',
      description: 'Work with experienced, supportive colleagues'
    },
    {
      icon: Award,
      title: 'Training Provided',
      description: 'Learn professional cleaning techniques and best practices'
    },
    {
      icon: Heart,
      title: 'Flexible Schedule',
      description: 'Work arrangements that fit your lifestyle'
    }
  ];

  return (
    <div className="pt-24 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#0B7CB5]/10 to-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Join Our <span className="text-[#0B7CB5]">Team</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Be part of Richmond's most trusted cleaning service. We're always looking for dedicated professionals to join our growing team.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Why Work With <span className="text-[#0B7CB5]">Us?</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div
                  key={index}
                  className="text-center p-6 bg-gradient-to-b from-[#0B7CB5]/5 to-white rounded-xl hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                    <IconComponent className="w-8 h-8 text-[#0B7CB5]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 bg-gradient-to-b from-white to-[#0B7CB5]/5">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Submit Your <span className="text-[#0B7CB5]">Application</span>
              </h2>
              <p className="text-lg text-gray-600">
                Fill out the form below and we'll get back to you soon
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-2xl shadow-lg space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#0B7CB5]/20">
                  Personal Information
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="address">Street Address *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zipCode">ZIP Code *</Label>
                      <Input
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Work Experience */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#0B7CB5]/20">
                  Work Experience
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="experience">Previous Cleaning or Relevant Experience *</Label>
                    <Textarea
                      id="experience"
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="mt-1"
                      placeholder="Describe your relevant work experience, skills, and why you'd be a great fit..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="availability">Availability *</Label>
                    <div className="flex items-center space-x-2 mb-2 mt-1">
                      <input
                        type="checkbox"
                        id="openAvailability"
                        name="openAvailability"
                        checked={formData.openAvailability}
                        onChange={handleChange}
                        className="w-4 h-4 text-[#0B7CB5] border-gray-300 rounded focus:ring-[#0B7CB5]"
                      />
                      <Label htmlFor="openAvailability" className="cursor-pointer font-normal text-sm">
                        Open Availability
                      </Label>
                    </div>
                    {!formData.openAvailability && (
                      <Textarea
                        id="availability"
                        name="availability"
                        value={formData.availability}
                        onChange={handleChange}
                        required={!formData.openAvailability}
                        rows={2}
                        className="mt-1"
                        placeholder="e.g., Monday-Friday 9am-5pm, Weekends available, etc."
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* References */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#0B7CB5]/20">
                  References
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="reference1Name">Reference 1 Name</Label>
                      <Input
                        id="reference1Name"
                        name="reference1Name"
                        value={formData.reference1Name}
                        onChange={handleChange}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="reference1Phone">Reference 1 Phone</Label>
                      <Input
                        id="reference1Phone"
                        name="reference1Phone"
                        type="tel"
                        value={formData.reference1Phone}
                        onChange={handleChange}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="reference2Name">Reference 2 Name</Label>
                      <Input
                        id="reference2Name"
                        name="reference2Name"
                        value={formData.reference2Name}
                        onChange={handleChange}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="reference2Phone">Reference 2 Phone</Label>
                      <Input
                        id="reference2Phone"
                        name="reference2Phone"
                        type="tel"
                        value={formData.reference2Phone}
                        onChange={handleChange}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div>
                <Label htmlFor="additionalInfo">Additional Information</Label>
                <Textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows={3}
                  className="mt-1"
                  placeholder="Any additional information you'd like to share..."
                />
              </div>

              {/* Background Check Consent */}
              <div className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg">
                <input
                  type="checkbox"
                  id="backgroundConsent"
                  name="backgroundConsent"
                  checked={formData.backgroundConsent}
                  onChange={handleChange}
                  required
                  className="w-5 h-5 text-[#0B7CB5] border-gray-300 rounded focus:ring-[#0B7CB5] mt-0.5"
                />
                <Label htmlFor="backgroundConsent" className="cursor-pointer text-sm">
                  I consent to a background check as part of the employment process *
                </Label>
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full bg-[#0B7CB5] hover:bg-[#095a87] text-white text-lg py-6"
              >
                Submit Application
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}